import React, { useState, useEffect } from "react";
import ModalBase from "./ModalBase";
import { HiPaperAirplane } from "react-icons/hi";
import { Controller, useForm } from "react-hook-form";
import VMasker from "vanilla-masker";
import { fornecedorService } from "@/services/fornecedorService";
import { useFeedback } from "@/contexts/FeedbackContext";

interface ModalEnvioFornecedorProps {
  isOpen: boolean;
  onClose: (fornecedor?: any) => void;
  defaultCnpj?: string;
}

const ModalEnvioFornecedor: React.FC<ModalEnvioFornecedorProps> = ({
  isOpen,
  onClose,
  defaultCnpj,
}) => {
  const [sending, setSending] = useState(false);
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm();
  const { showError, showSuccess } = useFeedback();

  useEffect(() => {
    if (isOpen && defaultCnpj) {
      setValue("cnpj", VMasker.toPattern(defaultCnpj, "99.999.999/9999-99"));
    }
  }, [isOpen, defaultCnpj, setValue]);

  function handleClose(fornecedor: any = undefined) {
    onClose(fornecedor);
    reset();
  }

  const handleSend = async (data: any) => {
    setSending(true);
    try {
      const response: any = await fornecedorService.createFromReceita(
        data.cnpj,
      );

      // A API retorna o fornecedor diretamente, não em response.data
      const fornecedor = response?.data || response;

      showSuccess("Fornecedor cadastrado com sucesso!");
      handleClose(fornecedor);
    } catch (error: any) {
      showError(
        error?.response?.data?.message || "Erro ao adicionar fornecedor",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <ModalBase isOpen={isOpen} onClose={handleClose}>
      <form onSubmit={handleSubmit(handleSend)}>
        <div className="pb-6 border-b border-base-300 mb-6">
          <h2 className="text-xl font-bold mb-2 text-base-content flex items-center gap-2">
            Adicionar fornecedor
          </h2>
        </div>
        <Controller
          control={control}
          name="cnpj"
          rules={{
            required: true,
            validate: (value: string) => {
              if (value.length < 18) {
                return "CNPJ inválido";
              }
              return true;
            },
          }}
          render={({ field }: { field: any }) => (
            <div className="form-control flex flex-col gap-1">
              <label className="label block">
                <span className="label-text font-medium">CNPJ</span>
              </label>
              <input
                type="tel"
                className="input input-bordered"
                placeholder="00.000.000/0000-00"
                {...field}
                onChange={(e) => {
                  field.onChange(
                    VMasker.toPattern(e.target.value, "99.999.999/9999-99"),
                  );
                }}
              />
              <span className="text-error text-sm">
                {errors.cnpj?.message as string}
              </span>
            </div>
          )}
        />

        <div className="flex justify-end gap-2 border-t border-base-300 pt-4 mt-6">
          <button
            className="btn btn-ghost"
            onClick={handleClose}
            disabled={sending}
            type="button"
          >
            Cancelar
          </button>
          <button className="btn btn-primary" disabled={sending} type="submit">
            {sending ? (
              <>
                <span className="loading loading-spinner"></span>
                Aguarde...
              </>
            ) : (
              <>
                <HiPaperAirplane className="h-4 w-4" />
                Adicionar
              </>
            )}
          </button>
        </div>
      </form>
    </ModalBase>
  );
};

export default ModalEnvioFornecedor;
